import { EmbeddingService } from '@accredible-frontend-v2/embedding';
import { environment } from '@accredible-frontend-v2/envs';
import {
  GainsightIdentifyPayload,
  identifyGainsightUser,
} from '@accredible-frontend-v2/utils/gainsight-px';
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, tap } from 'rxjs/operators';
import { DashboardIssuer } from '../shared/models/department-details';
import { WebSocketProgressChannelService } from '../shared/services/ws-progress-channel/ws-progress-channel.service';
import { DashboardStoreActions } from './stores/dashboard.actions';
import { DashboardState } from './stores/dashboard.reducer';
import { selectIssuer } from './stores/dashboard.selectors';

@Component({
  selector: 'db-root',
  templateUrl: './dashboard.container.html',
  styleUrls: ['./dashboard.container.scss'],
})
export class DashboardContainer implements OnInit, AfterViewInit, OnDestroy {
  private readonly _dashboardStore = inject(Store<DashboardState>);
  private readonly _embedding = inject(EmbeddingService);
  private readonly _router = inject(Router);
  private readonly _webSocketProgressChannel = inject(WebSocketProgressChannelService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _document = inject(DOCUMENT);

  issuer$: Observable<DashboardIssuer> = this._dashboardStore.select(selectIssuer);

  isEmbeddedRoute: boolean;
  readonly version = environment.version;

  ngOnInit(): void {
    this._handleV1DataLoaded();
    this._gainsightIdentify();
  }

  ngAfterViewInit(): void {
    this._onNavigationEnd();
  }

  ngOnDestroy(): void {
    this._webSocketProgressChannel.closeConnection();
  }

  /**
   * This is a workaround...
   * The native scrollPositionRestoration Angular router setting does not work because
   * we are using mat-sidenav-container as our main page element.
   * Angular does not scroll automatically to the top when changing route and
   * using this material component.
   *
   * This method also runs recaptcha v3 everytime the user changes route.
   */
  private _onNavigationEnd(): void {
    const matSideNavContentEl = this._document.querySelector('mat-sidenav-content');
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((event: Event | RouterEvent) => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        // TODO V1(Matt): We can remove this logic once v1 is deprecated
        this.isEmbeddedRoute = this._isEmbedRoute(event.url);

        // Some old browsers do not support scrollTo
        if (matSideNavContentEl.scrollTo) {
          matSideNavContentEl.scrollTo(0, 0);
        }
      });
  }

  private _gainsightIdentify(): void {
    this.issuer$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        first((issuer) => !!issuer?.user?.id),
        tap((issuer) => {
          const { user, department: account, permissions } = issuer;

          identifyGainsightUser(environment.dashboardGainsightTagKey, <GainsightIdentifyPayload>{
            user: {
              id: user.id,
              firstName: user.name,
              email: user.email,
              admin: permissions.admin,
            },
            account: {
              id: account?.id,
              name: account?.name,
            },
          });
        }),
      )
      .subscribe();
  }

  private _handleV1DataLoaded(): void {
    this._embedding.v1DataLoaded$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        first((dataLoaded) => dataLoaded),
      )
      .subscribe(() => {
        // We need this to check if the user is logged in, because if he isn't
        // and this call runs, we get the "not logged in" dialog
        if (this._embedding.departmentId) {
          this._dashboardStore.dispatch(DashboardStoreActions.loadFeatureFlags());
          this._dashboardStore.dispatch(DashboardStoreActions.loadIssuer());
          this._dashboardStore.dispatch(DashboardStoreActions.loadDepartments());
          this._webSocketProgressChannel.connect();
        }
      });
  }

  // Currently, these routes have both a v1 menu sidebar and v2 interface
  private _isEmbedRoute(url: string): boolean {
    const embedRoutes = [
      {
        base: '/group/',
        routes: ['/info-appearance', '/general-settings', '/visibility', '/endorsement'],
      },
      { base: '', routes: ['/email-campaigns'] },
      { base: '', routes: ['/new-email-templates'] },
      { base: '', routes: ['/find-campaigns'] },
    ];

    return embedRoutes.some(({ base, routes }) => {
      // Check if the URL starts with the base path (if any) and includes one of the specific routes
      if (base) {
        return url.startsWith(base) && routes.some((route) => url.includes(route));
      }

      // For routes without a base path, just check if the URL includes the specific route
      return routes.some((route) => url.includes(route));
    });
  }
}
